import { useState, useEffect, useRef, useCallback } from 'react';
import { ApiError } from '../helpers/baseApi';

interface UseQueryOptions {
  skip?: boolean,
  pollingInterval?: number,
}

const useQuery = <T>(query: (...args: any[]) => Promise<T>, options?: UseQueryOptions) => {
  const { skip = false, pollingInterval } = options || {};
  const [data, setData] = useState<T>();
  const [error, setError] = useState<ApiError>();
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await query();
      setData(response);
    } catch (error) {
      setError(error as ApiError);
    } finally {
      setIsLoading(false);
    }
  }, [query, pollingInterval]);

  useEffect(() => {
    if (skip || error) return;

    setIsLoading(true);
    fetchData();

    if (pollingInterval) {
      timerRef.current = setInterval(fetchData, pollingInterval);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [fetchData, error, skip]);

  return { data, error, isLoading };
};

const useMutation = <T>(mutation: (...args: any[]) => Promise<T>) => {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<ApiError>();
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async (...args: any[]) => {
    setIsLoading(true);

    try {
      const response = await mutation(...args);
      setData(response);
      return response;
    } catch (error) {
      setError(error as ApiError);
      throw error as ApiError;
    } finally {
      setIsLoading(false);
    }
  }, [mutation]);

  const reset = useCallback(() => {
    setData(undefined);
    setError(undefined);
    setIsLoading(false);
  }, []);

  return { data, error, isError: !!error, isSuccess: !!data, isLoading, mutate: fetchData, reset };
};

export { useQuery, useMutation, useMutation as useLazyQuery };
